import { useLayoutEffect, useRef } from "preact/hooks";
import { usePortalTarget } from "../usePortalTarget";

let tooltipCount = 0;

const useTooltips = () => {
    const cleanups = useRef<(() => void)[]>([]);
    const count = useRef(0);
    const target = usePortalTarget();

    useLayoutEffect(() => {
        count.current += 1;

        const tooltips = document.querySelectorAll<HTMLElement>(
            "[data-tooltip]:not([data-tooltip-done])",
        );

        const getTransform = (
            element: HTMLElement,
            parent: HTMLElement,
        ): string => {
            const rect = parent.getBoundingClientRect();
            const margin = 4;
            return `translate(${rect.left - element.scrollWidth / 2 + rect.width / 2}px, ${rect.top - element.scrollHeight / 2 - rect.height - margin}px)`;
        };

        const showTooltip = (parent: HTMLElement, tooltipId: string) => {
            const t = target.peek();

            if (!t) {
                return;
            }

            const tooltipText = String(parent.getAttribute("data-tooltip"));
            const tooltipElement = document.createElement("div");

            tooltipElement.id = tooltipId;
            tooltipElement.className =
                "transition-[visibility,opacity,color] bg-gradient-decor-to-r text-white text-xs rounded p-2 fixed top-0 left-0 z-[110] shadow-lg w-[200px]";
            tooltipElement.setAttribute("role", "tooltip");
            tooltipElement.setAttribute("data-tooltip-element", "true");
            tooltipElement.innerText = tooltipText;

            parent.setAttribute("aria-describedby", tooltipId);

            t.appendChild(tooltipElement);

            tooltipElement.style.transform = getTransform(
                tooltipElement,
                parent,
            );
        };

        const hideTooltip = (parent: HTMLElement, tooltipId: string) => {
            const tooltipElement = document.querySelector<HTMLElement>(
                `#${tooltipId}`,
            );

            if (!tooltipElement) {
                return;
            }

            tooltipElement.remove();

            parent.removeAttribute("aria-describedby");
        };

        tooltips.forEach((tooltip) => {
            const tooltipId = `tooltip-${++tooltipCount}`;

            tooltip.setAttribute("data-tooltip-done", "true");

            const handleMouseEnter = () => showTooltip(tooltip, tooltipId);

            const handleMouseLeave = () => hideTooltip(tooltip, tooltipId);

            const handleFocus = () => showTooltip(tooltip, tooltipId);

            const handleBlur = () => hideTooltip(tooltip, tooltipId);

            tooltip.addEventListener("mouseenter", handleMouseEnter);
            tooltip.addEventListener("mouseleave", handleMouseLeave);
            tooltip.addEventListener("focus", handleFocus);
            tooltip.addEventListener("blur", handleBlur);

            cleanups.current.push(() => {
                tooltip.removeAttribute("data-tooltip-done");
                tooltip.removeEventListener("mouseenter", handleMouseEnter);
                tooltip.removeEventListener("mouseleave", handleMouseLeave);
                tooltip.removeEventListener("focus", handleFocus);
                tooltip.removeEventListener("blur", handleBlur);
                tooltip.removeAttribute("data-tooltip-done");
                hideTooltip(tooltip, tooltipId);
            });
        });

        return () => {
            count.current -= 1;

            if (count.current === 0) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                cleanups.current.forEach((cleanup) => cleanup());
            }
        };
    });
};

export default useTooltips;
